/**
 * LogsPage Component
 * 
 * Dedicated Log Management interface for bank automation.
 * Features:
 * - Real-time and persistent log monitoring from MySQL
 * - Type-based filtering (LOGIN_FAILED, CLICK_FAILED, DATE_FILTER_ERROR, etc.)
 * - Date-based filtering with Quick Presets and custom Date & Time range
 * - Prominent, clean date and timestamp displays
 * - Search by keyword across message, bank, and account
 * - Live stats summary cards for quick triage
 * - Auto-refresh toggle (every 10s) and manual refresh
 * - Detailed error inspection modal with copy functionality
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Tag,
  Select,
  DatePicker,
  Input,
  Button,
  Tooltip,
  Switch,
  Modal,
  Empty,
  message,
  Typography,
  Badge,
  Tabs,
  Radio,
  Divider
} from 'antd';
import {
  ReloadOutlined,
  FilterOutlined,
  ClearOutlined,
  SearchOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  EyeOutlined,
  BugOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  AimOutlined,
  FileTextOutlined,
  RobotOutlined,
  MobileOutlined,
  GlobalOutlined,
  BankOutlined,
  DeleteOutlined,
  UserOutlined,
  ThunderboltOutlined,
  ExclamationCircleOutlined,
  CreditCardOutlined,
  WalletOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  getAutomationLogs,
  getBankList,
  clearAutomationLogs,
  getUserRole
} from '../../services/api';
import styles from './LogsPage.module.css';

dayjs.extend(relativeTime);

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

// Configuration for Log Types: labels, colors, and icons
const LOG_TYPE_CONFIG = {
  LOGIN_FAILED: {
    color: 'red',
    label: 'Login Failed',
    icon: <CloseCircleOutlined />,
    description: 'Bank login or netbanking authentication failed',
  },
  CLICK_FAILED: {
    color: 'volcano',
    label: 'Click Failed',
    icon: <AimOutlined />,
    description: 'Failed clicking target element, button, or dropdown',
  },
  DATE_FILTER_ERROR: {
    color: 'purple',
    label: 'Date Filter Error',
    icon: <CalendarOutlined />,
    description: 'Calendar popup or date filter failed to apply',
  },
  STATEMENT_ERROR: {
    color: 'cyan',
    label: 'Statement Error',
    icon: <FileTextOutlined />,
    description: 'Statement download, parsing, or file save error',
  },
  CAPTCHA_FAILED: {
    color: 'magenta',
    label: 'Captcha Failed',
    icon: <RobotOutlined />,
    description: 'Captcha solving service failed or timed out',
  },
  OTP_FAILED: {
    color: 'gold',
    label: 'OTP Failed',
    icon: <MobileOutlined />,
    description: 'Failed to retrieve or enter OTP verification code',
  },
  PROXY_ERROR: {
    color: 'orange',
    label: 'Proxy Error',
    icon: <GlobalOutlined />,
    description: 'Browser proxy or network connection failed',
  },
  USER_ALREADY_LOGGED: {
    color: 'geekblue',
    label: 'User Already Logged',
    icon: <UserOutlined />,
    description: 'User already active or concurrent netbanking session detected',
  },
  SERVER_SIDE_ERROR: {
    color: '#d4380d',
    label: 'Bank Server Error',
    icon: <ExclamationCircleOutlined />,
    description: 'Bank portal returned 500 error or is undergoing maintenance',
  },
  SEARCH_ERROR: {
    color: 'blue',
    label: 'Search Error',
    icon: <SearchOutlined />,
    description: 'Transaction search or filter criteria query failed',
  },
  TIMEOUT_ERROR: {
    color: 'volcano',
    label: 'Timeout Error',
    icon: <ClockCircleOutlined />,
    description: 'Browser operation or selector wait timed out',
  },
  SYSTEM_ERROR: {
    color: '#cf1322',
    label: 'System Error',
    icon: <BugOutlined />,
    description: 'Worker crash, exception, or unhandled automation error',
  },
  ERROR: {
    color: 'red',
    label: 'Error',
    icon: <CloseCircleOutlined />,
    description: 'General automation error',
  },
  WARNING: {
    color: 'warning',
    label: 'Warning',
    icon: <WarningOutlined />,
    description: 'Non-fatal operational warning',
  },
  INFO: {
    color: 'processing',
    label: 'Info',
    icon: <InfoCircleOutlined />,
    description: 'Operational worker event',
  },
};

const LogsPage = () => {
  const userRole = getUserRole();
  const isAdmin = userRole === 'ADMIN';

  // Main Data States
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    by_type: {},
    today_errors: 0,
    total_errors: 0,
    login_failed_count: 0,
    click_failed_count: 0,
    date_filter_error_count: 0,
  });

  // Module & Filter States
  const [moduleType, setModuleType] = useState('PAYIN'); // 'PAYIN', 'PAYOUT', or 'ALL'
  const [selectedType, setSelectedType] = useState('ALL');
  const [selectedLevel, setSelectedLevel] = useState('ALL');
  const [selectedBank, setSelectedBank] = useState('ALL');
  const [dateRange, setDateRange] = useState(null); // [dayjs, dayjs]
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  // Additional options & features
  const [accounts, setAccounts] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedLogForModal, setSelectedLogForModal] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [cleanupModalVisible, setCleanupModalVisible] = useState(false);
  const [cleanupOption, setCleanupOption] = useState('7_DAYS');
  const [cleanupScope, setCleanupScope] = useState('CURRENT');
  const [cleaningUp, setCleaningUp] = useState(false);

  // Accounts filtered by selected module (Payin vs Payout)
  const filteredAccounts = useMemo(() => {
    if (moduleType === 'PAYIN') {
      return accounts.filter(acc => !acc.payout_system);
    }
    if (moduleType === 'PAYOUT') {
      return accounts.filter(acc => !!acc.payout_system);
    }
    return accounts;
  }, [accounts, moduleType]);

  // List of unique bank names available within the current module
  const availableBanks = useMemo(() => {
    const bankSet = new Set();
    filteredAccounts.forEach(acc => {
      if (acc.name) bankSet.add(acc.name.toLowerCase());
    });
    logs.forEach(l => {
      if (l.bank_name) bankSet.add(l.bank_name.toLowerCase());
    });
    return Array.from(bankSet).sort();
  }, [filteredAccounts, logs]);

  // Accounts further filtered by selectedBank if one is chosen
  const selectableAccounts = useMemo(() => {
    if (selectedBank && selectedBank !== 'ALL') {
      return filteredAccounts.filter(acc => (acc.name || '').toLowerCase() === selectedBank.toLowerCase());
    }
    return filteredAccounts;
  }, [filteredAccounts, selectedBank]);

  // Quick Presets for Date Range
  const datePresets = useMemo(() => [
    { label: 'Today', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
    { label: 'Yesterday', value: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')] },
    { label: 'Last 7 Days', value: [dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')] },
    { label: 'Last 30 Days', value: [dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day')] },
    { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
  ], []);

  // Fetch bank accounts for the account filter dropdown
  const loadAccounts = useCallback(async () => {
    try {
      const resp = await getBankList();
      const list = Array.isArray(resp.data) ? resp.data : [];
      setAccounts(list);
    } catch (e) {
      console.error('Failed to load accounts for filter:', e);
    }
  }, []);

  // Fetch logs with current filters
  const fetchLogs = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
      };

      if (moduleType && moduleType !== 'ALL') {
        params.module_type = moduleType;
      }

      if (selectedType && selectedType !== 'ALL') {
        params.log_type = selectedType;
      }

      if (selectedLevel && selectedLevel !== 'ALL') {
        params.level = selectedLevel;
      }

      if (selectedBank && selectedBank !== 'ALL') {
        params.bank_name = selectedBank;
      }

      if (selectedAccountId) {
        params.account_id = selectedAccountId;
      }

      if (dateRange && dateRange[0] && dateRange[1]) {
        params.start_date = dateRange[0].toISOString();
        params.end_date = dateRange[1].toISOString();
      }

      if (searchKeyword && searchKeyword.trim()) {
        params.search = searchKeyword.trim();
      }

      const response = await getAutomationLogs(params);
      const data = response.data;

      setLogs(data.data || []);
      setTotalCount(data.total || 0);
      setPagination({ current: data.page || page, pageSize: data.page_size || pageSize });

      if (data.stats) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch automation logs:', err);
      message.error('Failed to load logs: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [moduleType, selectedType, selectedLevel, selectedBank, selectedAccountId, dateRange, searchKeyword]);

  // Initial load
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Trigger fetch when filters or pagination changes
  useEffect(() => {
    fetchLogs(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLogs, pagination.current, pagination.pageSize]);

  // Auto-refresh timer
  useEffect(() => {
    let timer = null;
    if (autoRefresh) {
      timer = setInterval(() => {
        fetchLogs(pagination.current, pagination.pageSize);
      }, 10000); // every 10s
    }
    return () => {
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, fetchLogs, pagination.current, pagination.pageSize]);

  // Reset all filters
  const handleResetFilters = () => {
    setSelectedType('ALL');
    setSelectedLevel('ALL');
    setSelectedBank('ALL');
    setDateRange(null);
    setSelectedAccountId(null);
    setSearchKeyword('');
    setPagination({ current: 1, pageSize: 20 });
  };

  // Quick filter by KPI card
  const handleKpiCardClick = (type) => {
    if (selectedType === type) {
      setSelectedType('ALL');
    } else {
      setSelectedType(type);
    }
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Copy message helper
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Log message copied to clipboard');
  };

  // Feature #2: 1-Click CSV Log Export
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const exportParams = {
        page: 1,
        page_size: 2500, // Export up to 2500 records
      };

      if (moduleType && moduleType !== 'ALL') exportParams.module_type = moduleType;
      if (selectedType && selectedType !== 'ALL') exportParams.log_type = selectedType;
      if (selectedLevel && selectedLevel !== 'ALL') exportParams.level = selectedLevel;
      if (selectedBank && selectedBank !== 'ALL') exportParams.bank_name = selectedBank;
      if (selectedAccountId) exportParams.account_id = selectedAccountId;
      if (dateRange && dateRange[0] && dateRange[1]) {
        exportParams.start_date = dateRange[0].toISOString();
        exportParams.end_date = dateRange[1].toISOString();
      }
      if (searchKeyword && searchKeyword.trim()) {
        exportParams.search = searchKeyword.trim();
      }

      const resp = await getAutomationLogs(exportParams);
      const records = resp.data?.data || logs;

      if (!records || records.length === 0) {
        message.warning('No logs available to export for current filters');
        return;
      }

      const headers = [
        'Log ID',
        'Date (Local)',
        'Time (Local)',
        'UTC Timestamp',
        'Module',
        'Log Type',
        'Severity Level',
        'Bank Name',
        'Account Name',
        'Account ID',
        'Profile ID',
        'Project Name',
        'Message',
        'Details'
      ];

      const escapeField = (val) => {
        if (val === null || val === undefined) return '""';
        return `"${String(val).replace(/"/g, '""')}"`;
      };

      const rows = records.map(r => {
        const d = dayjs(r.created_at);
        return [
          r.id,
          escapeField(d.format('YYYY-MM-DD')),
          escapeField(d.format('hh:mm:ss A')),
          escapeField(r.created_at),
          escapeField(r.module_type || 'PAYIN'),
          escapeField(r.log_type || 'ERROR'),
          escapeField(r.level || 'ERROR'),
          escapeField((r.bank_name || '').toUpperCase()),
          escapeField(r.account_name || ''),
          escapeField(r.account_id || ''),
          escapeField(r.profile_id || ''),
          escapeField(r.project_name || ''),
          escapeField(r.message || ''),
          escapeField(r.details || '')
        ].join(',');
      });

      // UTF-8 BOM (\uFEFF) ensures Excel reads UTF-8 correctly
      const csvContent = '\uFEFF' + [headers.map(escapeField).join(','), ...rows].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileStamp = dayjs().format('YYYYMMDD_HHmmss');
      link.setAttribute('href', url);
      link.setAttribute('download', `automation_logs_${moduleType.toLowerCase()}_${fileStamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success(`Exported ${records.length} logs to CSV successfully`);
    } catch (err) {
      console.error('CSV Export Error:', err);
      message.error('Export failed: ' + (err.message || 'Unknown error'));
    } finally {
      setExporting(false);
    }
  };

  // Feature #3: Smart Log Retention & Cleanup Handler
  const handleExecuteCleanup = async () => {
    setCleaningUp(true);
    try {
      const params = {};

      if (cleanupOption === '7_DAYS') {
        params.before_date = dayjs().subtract(7, 'day').toISOString();
      } else if (cleanupOption === '14_DAYS') {
        params.before_date = dayjs().subtract(14, 'day').toISOString();
      } else if (cleanupOption === '30_DAYS') {
        params.before_date = dayjs().subtract(30, 'day').toISOString();
      } // If 'ALL', leaves before_date empty to clear all

      if (cleanupScope === 'CURRENT' && moduleType !== 'ALL') {
        params.module_type = moduleType;
      }

      const resp = await clearAutomationLogs(params);
      const count = resp.data?.deleted_count ?? 0;
      message.success(`Successfully deleted ${count} historical logs`);
      setCleanupModalVisible(false);
      fetchLogs(1, pagination.pageSize);
    } catch (err) {
      console.error('Failed to clear logs:', err);
      message.error('Failed to clear logs: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCleaningUp(false);
    }
  };

  // Table Columns Definition
  const columns = [
    {
      title: (
        <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <CalendarOutlined style={{ color: '#1677ff' }} />
          Date & Time
        </span>
      ),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 195,
      render: (val) => {
        if (!val) return '-';
        const d = dayjs(val);
        return (
          <Tooltip title={`UTC: ${d.toISOString()} | Local: ${d.format('YYYY-MM-DD HH:mm:ss')}`}>
            <div className={styles.dateDisplay}>
              <div className={styles.dateMain}>
                <CalendarOutlined style={{ color: '#1890ff', fontSize: 13 }} />
                <span>{d.format('DD MMM YYYY')}</span>
              </div>
              <div className={styles.timeSecondary}>
                <ClockCircleOutlined style={{ fontSize: 11 }} />
                <span>{d.format('hh:mm:ss A')}</span>
                <span className={styles.relativeTime}>({d.fromNow()})</span>
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Module',
      dataIndex: 'module_type',
      key: 'module_type',
      width: 110,
      render: (type) => {
        const isPayout = (type || '').toUpperCase() === 'PAYOUT';
        return (
          <Tag
            color={isPayout ? 'purple' : 'blue'}
            style={{ fontWeight: 600, fontSize: 11, borderRadius: 4 }}
          >
            {isPayout ? '💸 PAYOUT' : '💳 PAYIN'}
          </Tag>
        );
      },
    },
    {
      title: 'Log Type',
      dataIndex: 'log_type',
      key: 'log_type',
      width: 175,
      render: (type, record) => {
        const config = LOG_TYPE_CONFIG[type] || {
          color: record.level === 'ERROR' ? 'red' : record.level === 'WARNING' ? 'orange' : 'default',
          label: type,
          icon: record.level === 'ERROR' ? <CloseCircleOutlined /> : <InfoCircleOutlined />
        };
        return (
          <Tooltip title={config.description || type}>
            <Tag color={config.color} style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4 }}>
              {config.icon} <span style={{ marginLeft: 4 }}>{config.label}</span>
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Bank & Account',
      dataIndex: 'bank_name',
      key: 'bank_name',
      width: 190,
      render: (bank, record) => (
        <div>
          {bank && (
            <Tag color="blue" style={{ textTransform: 'uppercase', fontWeight: 600, marginBottom: 2 }}>
              <BankOutlined style={{ marginRight: 4 }} />
              {bank}
            </Tag>
          )}
          <div style={{ fontSize: 12, color: '#4b5563', fontWeight: 500 }}>
            {record.account_name || (record.account_id ? `Account #${record.account_id}` : 'General / System')}
          </div>
        </div>
      ),
    },
    {
      title: 'Profile ID',
      dataIndex: 'profile_id',
      key: 'profile_id',
      width: 120,
      render: (profileId) =>
        profileId ? (
          <Tag color="default" style={{ fontFamily: 'monospace', fontSize: 11 }}>
            {profileId}
          </Tag>
        ) : (
          <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>
        ),
    },
    {
      title: 'Message & Error Details',
      dataIndex: 'message',
      key: 'message',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div className={styles.messageCell}>
            <Paragraph
              ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
              style={{ margin: 0, color: record.level === 'ERROR' ? '#b91c1c' : '#374151' }}
            >
              {text}
            </Paragraph>
          </div>
          <Tooltip title="Copy Message">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopy(text)}
              style={{ color: '#6b7280' }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 90,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="View Full Log Details">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setSelectedLogForModal(record)}
          >
            Details
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      {/* Top Header */}
      <div className={styles.header}>
        <div className={styles.titleArea}>
          <BugOutlined style={{ fontSize: 26, color: '#2563eb' }} />
          <div>
            <h2>Automation Log Management</h2>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Persistent activity and error audit trail for Selenium workers (Login, Click, Date Filters, and Execution).
            </Text>
          </div>
        </div>

        <div className={styles.headerActions}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#4b5563' }}>Auto-Refresh (10s):</span>
            <Switch
              checked={autoRefresh}
              onChange={setAutoRefresh}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>

          <Button
            icon={<ReloadOutlined spin={loading} />}
            onClick={() => fetchLogs(pagination.current, pagination.pageSize)}
            loading={loading}
          >
            Refresh
          </Button>

          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            loading={exporting}
            style={{ fontWeight: 500 }}
          >
            Export CSV
          </Button>

          {isAdmin && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => setCleanupModalVisible(true)}
            >
              Log Retention
            </Button>
          )}
        </div>
      </div>

      {/* Module Separation Tabs: Payin vs Payout */}
      <div className={styles.moduleTabsContainer}>
        <Tabs
          activeKey={moduleType}
          onChange={(key) => {
            setModuleType(key);
            setSelectedAccountId(null);
            setSelectedType('ALL');
            setSelectedLevel('ALL');
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          type="card"
          size="middle"
          items={[
            {
              key: 'PAYIN',
              label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                  <CreditCardOutlined style={{ color: '#1677ff', fontSize: 16 }} />
                  <span>Payin Automation Logs</span>
                  {moduleType === 'PAYIN' && (
                    <Badge count={totalCount} overflowCount={99999} style={{ backgroundColor: '#1677ff' }} />
                  )}
                </span>
              ),
            },
            {
              key: 'PAYOUT',
              label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                  <WalletOutlined style={{ color: '#722ed1', fontSize: 16 }} />
                  <span>Payout Automation Logs</span>
                  {moduleType === 'PAYOUT' && (
                    <Badge count={totalCount} overflowCount={99999} style={{ backgroundColor: '#722ed1' }} />
                  )}
                </span>
              ),
            },
            {
              key: 'ALL',
              label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                  <ThunderboltOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
                  <span>All Automation Logs</span>
                  {moduleType === 'ALL' && (
                    <Badge count={totalCount} overflowCount={99999} style={{ backgroundColor: '#fa8c16' }} />
                  )}
                </span>
              ),
            },
          ]}
        />
      </div>

      {/* Primary KPI Overview Cards */}
      <div className={styles.statsGrid}>
        <div
          className={`${styles.statCard} ${selectedType === 'ALL' && selectedLevel === 'ALL' ? styles.statCardActive : ''}`}
          onClick={() => { setSelectedType('ALL'); setSelectedLevel('ALL'); setPagination(prev => ({ ...prev, current: 1 })); }}
        >
          <div>
            <div className={styles.statLabel}>Total Logs</div>
            <div className={styles.statValue}>{totalCount}</div>
          </div>
          <FileTextOutlined className={styles.statIcon} style={{ color: '#2563eb' }} />
        </div>

        <div
          className={`${styles.statCard} ${selectedLevel === 'ERROR' && selectedType === 'ALL' ? styles.statCardActive : ''}`}
          onClick={() => {
            if (selectedLevel === 'ERROR') {
              setSelectedLevel('ALL');
            } else {
              setSelectedLevel('ERROR');
              setSelectedType('ALL');
            }
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
        >
          <div>
            <div className={styles.statLabel}>Total Errors</div>
            <div className={styles.statValue} style={{ color: '#dc2626' }}>
              {stats.total_errors ?? 0}
            </div>
          </div>
          <CloseCircleOutlined className={styles.statIcon} style={{ color: '#dc2626' }} />
        </div>

        <div
          className={`${styles.statCard} ${dateRange && selectedLevel === 'ERROR' ? styles.statCardActive : ''}`}
          onClick={() => {
            setDateRange([dayjs().startOf('day'), dayjs().endOf('day')]);
            setSelectedLevel('ERROR');
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
        >
          <div>
            <div className={styles.statLabel}>Today's Errors</div>
            <div className={styles.statValue} style={{ color: '#ea580c' }}>
              {stats.today_errors ?? 0}
            </div>
          </div>
          <WarningOutlined className={styles.statIcon} style={{ color: '#ea580c' }} />
        </div>

        <div className={styles.statCard} style={{ cursor: 'default' }}>
          <div>
            <div className={styles.statLabel}>Accounts with Errors</div>
            <div className={styles.statValue} style={{ color: '#7c3aed' }}>
              {stats.affected_accounts ?? 0}
            </div>
          </div>
          <BankOutlined className={styles.statIcon} style={{ color: '#7c3aed' }} />
        </div>
      </div>

      {/* Discovered Error Categories from Selenium Automation (Dynamic) */}
      <div className={styles.dynamicSection}>
        <div className={styles.dynamicSectionHeader}>
          <div className={styles.dynamicSectionTitle}>
            <ThunderboltOutlined style={{ color: '#d97706' }} />
            <span>Discovered Error Categories from Selenium Automation</span>
            <span style={{ fontSize: 11, color: '#6b7280', textTransform: 'none', fontWeight: 400 }}>
              (Live categories detected from worker logs)
            </span>
          </div>
          {selectedType !== 'ALL' && (
            <Button type="link" size="small" onClick={() => setSelectedType('ALL')}>
              Reset Category Filter
            </Button>
          )}
        </div>

        <div className={styles.dynamicPillList}>
          <div
            className={`${styles.dynamicPill} ${selectedType === 'ALL' ? styles.dynamicPillActive : ''}`}
            onClick={() => handleKpiCardClick('ALL')}
          >
            <span>All Categories</span>
            <span className={styles.dynamicPillBadge}>{totalCount}</span>
          </div>

          {Object.entries(stats.by_type || {}).map(([type, count]) => {
            const config = LOG_TYPE_CONFIG[type] || {
              color: 'default',
              label: type.replace(/_/g, ' '),
              icon: <BugOutlined />
            };
            const isActive = selectedType === type;
            return (
              <div
                key={type}
                className={`${styles.dynamicPill} ${isActive ? styles.dynamicPillActive : ''}`}
                onClick={() => handleKpiCardClick(type)}
              >
                {config.icon}
                <span>{config.label}</span>
                <span
                  className={styles.dynamicPillBadge}
                  style={isActive ? { background: '#2563eb', color: '#fff' } : {}}
                >
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className={styles.filterBar}>
        {/* Log Type Filter */}
        <div className={styles.filterItem} style={{ minWidth: 200 }}>
          <span className={styles.filterLabel}>Log Type</span>
          <Select
            value={selectedType}
            onChange={(val) => {
              setSelectedType(val);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ width: '100%' }}
          >
            <Option value="ALL">All Types</Option>
            <Option value="LOGIN_FAILED">
              <Tag color="red" style={{ marginRight: 4 }}>Login Failed</Tag>
            </Option>
            <Option value="CLICK_FAILED">
              <Tag color="volcano" style={{ marginRight: 4 }}>Click Failed</Tag>
            </Option>
            <Option value="DATE_FILTER_ERROR">
              <Tag color="purple" style={{ marginRight: 4 }}>Date Filter Error</Tag>
            </Option>
            <Option value="STATEMENT_ERROR">
              <Tag color="cyan" style={{ marginRight: 4 }}>Statement Error</Tag>
            </Option>
            <Option value="CAPTCHA_FAILED">
              <Tag color="magenta" style={{ marginRight: 4 }}>Captcha Error</Tag>
            </Option>
            <Option value="OTP_FAILED">
              <Tag color="gold" style={{ marginRight: 4 }}>OTP Error</Tag>
            </Option>
            <Option value="PROXY_ERROR">
              <Tag color="orange" style={{ marginRight: 4 }}>Proxy Error</Tag>
            </Option>
            <Option value="USER_ALREADY_LOGGED">
              <Tag color="geekblue" style={{ marginRight: 4 }}>User Already Logged</Tag>
            </Option>
            <Option value="SERVER_SIDE_ERROR">
              <Tag color="#d4380d" style={{ marginRight: 4 }}>Bank Server Error</Tag>
            </Option>
            <Option value="SEARCH_ERROR">
              <Tag color="blue" style={{ marginRight: 4 }}>Search Error</Tag>
            </Option>
            <Option value="TIMEOUT_ERROR">
              <Tag color="volcano" style={{ marginRight: 4 }}>Timeout Error</Tag>
            </Option>
            <Option value="SYSTEM_ERROR">
              <Tag color="#cf1322" style={{ marginRight: 4 }}>System Error</Tag>
            </Option>
            <Option value="WARNING">
              <Tag color="warning" style={{ marginRight: 4 }}>Warning</Tag>
            </Option>
            <Option value="INFO">
              <Tag color="processing" style={{ marginRight: 4 }}>Info</Tag>
            </Option>
          </Select>
        </div>

        {/* Date-Based Filter */}
        <div className={styles.filterItem} style={{ minWidth: 260 }}>
          <span className={styles.filterLabel}>Date Filter (Presets & Custom)</span>
          <RangePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            presets={datePresets}
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ width: '100%' }}
            placeholder={['Start Date', 'End Date']}
          />
        </div>

        {/* Bank-Level Filter (Feature #1) */}
        <div className={styles.filterItem} style={{ minWidth: 160 }}>
          <span className={styles.filterLabel}>Bank</span>
          <Select
            value={selectedBank}
            onChange={(val) => {
              setSelectedBank(val);
              setSelectedAccountId(null);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ width: '100%' }}
          >
            <Option value="ALL">All Banks</Option>
            {availableBanks.map(b => (
              <Option key={b} value={b}>
                <span style={{ textTransform: 'uppercase', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BankOutlined style={{ color: '#1677ff' }} />
                  {b}
                </span>
              </Option>
            ))}
          </Select>
        </div>

        {/* Account Filter */}
        <div className={styles.filterItem} style={{ minWidth: 200 }}>
          <span className={styles.filterLabel}>Bank Account</span>
          <Select
            allowClear
            showSearch
            placeholder="All Accounts"
            value={selectedAccountId}
            onChange={(val) => {
              setSelectedAccountId(val);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            filterOption={(input, option) =>
              (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: '100%' }}
          >
            {selectableAccounts.map(acc => (
              <Option key={acc.id} value={acc.id}>
                {acc.name?.toUpperCase()} - {acc.account_name || `#${acc.id}`} {acc.payout_system ? '💸 [Payout]' : '💳 [Payin]'}
              </Option>
            ))}
          </Select>
        </div>

        {/* Search Filter */}
        <div className={styles.filterItem} style={{ flex: 1, minWidth: 200 }}>
          <span className={styles.filterLabel}>Search Message</span>
          <Input
            placeholder="Search keywords (e.g. timeout, invalid password, calendar)..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={() => setPagination(prev => ({ ...prev, current: 1 }))}
            allowClear
          />
        </div>

        {/* Clear Filters Button */}
        <div style={{ alignSelf: 'flex-end', paddingTop: 20 }}>
          <Button
            icon={<ClearOutlined />}
            onClick={handleResetFilters}
          >
            Reset Filters
          </Button>
        </div>
      </div>

      {/* Active Date & Filter Banner */}
      {(selectedType !== 'ALL' || selectedBank !== 'ALL' || dateRange || selectedAccountId || searchKeyword) && (
        <div className={styles.banner}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <FilterOutlined />
            <span>Active Filters:</span>
            {selectedType !== 'ALL' && (
              <Tag color="blue" closable onClose={() => setSelectedType('ALL')}>
                Type: {selectedType}
              </Tag>
            )}
            {selectedBank !== 'ALL' && (
              <Tag color="purple" closable onClose={() => setSelectedBank('ALL')}>
                <BankOutlined style={{ marginRight: 4 }} />
                Bank: {selectedBank.toUpperCase()}
              </Tag>
            )}
            {dateRange && dateRange[0] && dateRange[1] && (
              <Tag color="geekblue" closable onClose={() => setDateRange(null)}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                From: {dateRange[0].format('DD MMM YYYY, HH:mm')} To: {dateRange[1].format('DD MMM YYYY, HH:mm')}
              </Tag>
            )}
            {selectedAccountId && (
              <Tag color="cyan" closable onClose={() => setSelectedAccountId(null)}>
                Account ID: {selectedAccountId}
              </Tag>
            )}
            {searchKeyword && (
              <Tag color="default" closable onClose={() => setSearchKeyword('')}>
                Query: "{searchKeyword}"
              </Tag>
            )}
          </div>
          <Button type="link" size="small" onClick={handleResetFilters}>
            Clear All
          </Button>
        </div>
      )}

      {/* Dedicated Log Table */}
      <div className={styles.tableCard}>
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} logs`,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <span>
                    No automation logs found matching the selected filters.
                  </span>
                }
              >
                <Button type="primary" onClick={handleResetFilters}>
                  Clear Filters
                </Button>
              </Empty>
            ),
          }}
        />
      </div>

      {/* Log Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BugOutlined style={{ color: '#2563eb' }} />
            <span>Log Entry Details (ID #{selectedLogForModal?.id})</span>
          </div>
        }
        open={!!selectedLogForModal}
        onCancel={() => setSelectedLogForModal(null)}
        footer={[
          <Button
            key="copy"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(JSON.stringify(selectedLogForModal, null, 2))}
          >
            Copy JSON
          </Button>,
          <Button key="close" type="primary" onClick={() => setSelectedLogForModal(null)}>
            Close
          </Button>,
        ]}
        width={720}
      >
        {selectedLogForModal && (
          <div className={styles.detailsModalContent}>
            <div className={styles.detailRow}>
              <div className={styles.detailLabel}>Module:</div>
              <div className={styles.detailValue}>
                <Tag
                  color={(selectedLogForModal.module_type || '').toUpperCase() === 'PAYOUT' ? 'purple' : 'blue'}
                  style={{ fontWeight: 600, fontSize: 12 }}
                >
                  {(selectedLogForModal.module_type || '').toUpperCase() === 'PAYOUT' ? '💸 PAYOUT AUTOMATION' : '💳 PAYIN AUTOMATION'}
                </Tag>
              </div>
            </div>

            <div className={styles.detailRow}>
              <div className={styles.detailLabel}>Log Type:</div>
              <div className={styles.detailValue}>
                <Tag color={LOG_TYPE_CONFIG[selectedLogForModal.log_type]?.color || 'red'}>
                  {selectedLogForModal.log_type}
                </Tag>
                <Tag color={selectedLogForModal.level === 'ERROR' ? 'red' : 'blue'}>
                  Level: {selectedLogForModal.level}
                </Tag>
              </div>
            </div>

            <div className={styles.detailRow}>
              <div className={styles.detailLabel}>Date & Time:</div>
              <div className={styles.detailValue}>
                <strong>{dayjs(selectedLogForModal.created_at).format('DD MMMM YYYY, hh:mm:ss A')}</strong>
                <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 12 }}>
                  ({dayjs(selectedLogForModal.created_at).fromNow()})
                </span>
                <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace', marginTop: 2 }}>
                  UTC: {selectedLogForModal.created_at}
                </div>
              </div>
            </div>

            <div className={styles.detailRow}>
              <div className={styles.detailLabel}>Bank & Account:</div>
              <div className={styles.detailValue}>
                <Tag color="blue">{selectedLogForModal.bank_name?.toUpperCase() || 'N/A'}</Tag>
                <span>{selectedLogForModal.account_name || 'N/A'} (ID: {selectedLogForModal.account_id || 'N/A'})</span>
              </div>
            </div>

            <div className={styles.detailRow}>
              <div className={styles.detailLabel}>Profile ID:</div>
              <div className={styles.detailValue}>
                <code>{selectedLogForModal.profile_id || 'N/A'}</code>
              </div>
            </div>

            {selectedLogForModal.project_name && (
              <div className={styles.detailRow}>
                <div className={styles.detailLabel}>Project:</div>
                <div className={styles.detailValue}>
                  {selectedLogForModal.project_name} (ID: {selectedLogForModal.project_id})
                </div>
              </div>
            )}

            <div className={styles.detailRow}>
              <div className={styles.detailLabel}>Full Message:</div>
              <div className={styles.detailValue} style={{ fontWeight: 500 }}>
                {selectedLogForModal.message}
              </div>
            </div>

            {selectedLogForModal.details && (
              <div>
                <div style={{ fontWeight: 600, color: '#4b5563', marginBottom: 6 }}>
                  Technical Details / Stack Trace:
                </div>
                <div className={styles.rawDetailsBox}>
                  {selectedLogForModal.details}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Feature #3: Smart Log Retention & Safe Cleanup Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#dc2626' }}>
            <DeleteOutlined style={{ fontSize: 18 }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Log Retention & Cleanup</span>
          </div>
        }
        open={cleanupModalVisible}
        onCancel={() => !cleaningUp && setCleanupModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setCleanupModalVisible(false)} disabled={cleaningUp}>
            Cancel
          </Button>,
          <Button
            key="submit"
            danger
            type="primary"
            loading={cleaningUp}
            onClick={handleExecuteCleanup}
            icon={<DeleteOutlined />}
          >
            Execute Cleanup
          </Button>,
        ]}
        width={560}
      >
        <div style={{ padding: '8px 0' }}>
          <Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 13 }}>
            Safely maintain database performance by pruning older log entries while preserving recent operational audit trails.
          </Paragraph>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 10, color: '#374151', fontSize: 13 }}>
              Select Retention Window:
            </div>
            <Radio.Group
              value={cleanupOption}
              onChange={(e) => setCleanupOption(e.target.value)}
              style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <Radio value="7_DAYS">
                <span style={{ fontWeight: 500 }}>Delete logs older than 7 days</span>
                <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
                  (Cutoff: {dayjs().subtract(7, 'day').format('DD MMM YYYY')})
                </span>
              </Radio>
              <Radio value="14_DAYS">
                <span style={{ fontWeight: 500 }}>Delete logs older than 14 days</span>
                <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
                  (Cutoff: {dayjs().subtract(14, 'day').format('DD MMM YYYY')})
                </span>
              </Radio>
              <Radio value="30_DAYS">
                <span style={{ fontWeight: 500 }}>Delete logs older than 30 days</span>
                <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>
                  (Cutoff: {dayjs().subtract(30, 'day').format('DD MMM YYYY')})
                </span>
              </Radio>
              <Radio value="ALL">
                <span style={{ color: '#dc2626', fontWeight: 600 }}>Clear all historical logs</span>
                <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 8 }}>
                  (Irreversible permanent wipe)
                </span>
              </Radio>
            </Radio.Group>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#374151', fontSize: 13 }}>
              Target Module Scope:
            </div>
            <Radio.Group
              value={cleanupScope}
              onChange={(e) => setCleanupScope(e.target.value)}
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              <Radio value="CURRENT">
                Only Active Tab (<strong>{moduleType === 'PAYOUT' ? '💸 Payout Logs' : moduleType === 'PAYIN' ? '💳 Payin Logs' : 'All Modules'}</strong>)
              </Radio>
              <Radio value="ALL">
                All Logs (Both Payin and Payout modules)
              </Radio>
            </Radio.Group>
          </div>

          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 14px', borderRadius: 6, fontSize: 12, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 6 }}>
            <WarningOutlined style={{ fontSize: 14 }} />
            <span>This action permanently deletes records from MySQL. Once executed, records cannot be restored.</span>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LogsPage;
